import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ConfirmModal from "../components/modals/ConfirmModal";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import { ACCOUNTING_PRICING_PACKAGES } from "../constants/accountingPricing";
import {
  applyBillingDiscountCode,
  cancelBillingSubscription,
  getBillingSummary,
  getBillingTransactions,
  reactivateBillingSubscription,
  removeBillingDiscountCode,
  syncBillingSeats,
  updateBillingTaxAddon,
} from "../api/billing";
import { formatInTimeZone } from "../utils/datetime";

const MODAL_TEXT = "text-sm md:text-base";

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page does",
    description: "Review your required Ellyanna Booking subscription, optional SalonBusinessCPA accounting services, card status, and billing transactions.",
    anchorId: "billing-overview",
    bullets: [
      "Independent professionals have their own monthly plan.",
      "Salon software plans are based on staff who are active and perform services.",
      "For the current billing period, Ellyanna uses the highest active service staff count recorded in that period.",
      "Ellyanna Booking is the required salon software subscription.",
      "SalonBusinessCPA is optional accounting, tax, payroll, and record support.",
      "Apply discount codes here when you want a temporary percent-off promotion.",
      "Use Manage Card on File to replace the saved card.",
    ],
    tips: ["Open invoice transactions to view invoice documents in a new tab."],
  },
  {
    id: "summary",
    title: "Summary panel",
    description: "Shows subscription details for the current salon.",
    anchorId: "billing-summary",
    bullets: [
      "Current staff count shows who is active today. Billing-period staff count shows the count used for this billing cycle.",
      "SalonBusinessCPA support is selected separately from the Ellyanna Booking plan.",
      "Next Billing Date is displayed in the salon timezone.",
    ],
    tips: ["If a salon has more than 10 active service staff, billing switches to custom quote."],
  },
  {
    id: "transactions",
    title: "Transactions",
    description: "View invoices and payments in chronological order.",
    anchorId: "billing-transactions",
    bullets: [
      "Invoice amounts show as positive values.",
      "Payment amounts show as negative values.",
      "Use Load More to fetch older transaction rows.",
    ],
    tips: ["Use View Statements for printable date-range statements."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Status shows no card on file",
    body: "Open Manage Card on File and replace the saved card.",
  },
  {
    title: "Staff count changed but plan looks outdated",
    body: "Use Sync Seats to refresh the current staff count and billing-period staff count.",
  },
  {
    title: "SalonBusinessCPA package did not update",
    body: "Select the package again and wait for the confirmation banner before leaving the page.",
  },
  {
    title: "Invoice link does not open",
    body: "Check browser pop-up blocking and allow opening a new tab.",
  },
];

const TOOLTIPS = {
  seat_count: "Staff who are active and set to perform services in this salon right now.",
  billing_period_staff_count: "The highest active service staff count recorded in the current billing period.",
  base_plan: "Your flat monthly Ellyanna software plan.",
  plan_range: "The staff range or independent professional category covered by your current plan.",
  monthly_rate: "Required Ellyanna Booking subscription plus any optional SalonBusinessCPA accounting package.",
  subtotal: "Current monthly amount before any active discount code is applied.",
  discount: "Temporary percentage discount applied through a Stripe promotion code.",
  status: "Current subscription state.",
  balance: "Open balance in the billing account currency.",
  next_billing: "Upcoming billing date and time shown in salon timezone.",
  manage_card: "Open the saved card details and replacement flow.",
  statements: "Open printable date-range statement view.",
  tax_addon: "Optional monthly SalonBusinessCPA accounting, tax, payroll, and record support. It is separate from the required Ellyanna Booking plan.",
  discount_code: "Enter a discount code provided by Ellyanna. The code is checked against its approved date window and usage limit.",
  seat_rules: "Salon plan tier uses the billing-period staff count, which protects against changing staff status only around billing dates. Independent professionals have their own plan.",
  transaction_date: "Transaction timestamp in salon timezone.",
  transaction_amount: "Invoice totals are positive and payments are negative.",
};

const TAX_ADDON_OPTIONS = [
  { code: null, label: "No SalonBusinessCPA package", priceCents: 0 },
  ...ACCOUNTING_PRICING_PACKAGES,
];

const PLAN_TIERS = [
  {
    code: "independent_professional",
    label: "Independent Professional",
    technicianRange: "Independent professional",
    priceCents: 3999,
  },
  {
    code: "starter",
    label: "Starter",
    technicianRange: "1 to 3 salon staff",
    priceCents: 7999,
  },
  {
    code: "growth",
    label: "Growth",
    technicianRange: "4 to 6 salon staff",
    priceCents: 12999,
  },
  {
    code: "scale",
    label: "Scale",
    technicianRange: "7 to 10 salon staff",
    priceCents: 18999,
  },
];

const formatCurrencyFromCents = (amountCents, currency = "cad") => {
  const normalized = (currency || "cad").toUpperCase();
  const value = Number(amountCents || 0) / 100;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: normalized,
  }).format(value);
};

const formatStatusLabel = (value) => {
  const normalized = (value || "").toLowerCase();
  if (!normalized || normalized === "no_card_on_file") return "No card on file";
  if (normalized === "trial_expired") return "Trial expired";
  if (normalized === "test_free") return "Testing account - free";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatTransactionLabel = (row) => {
  if (row?.type === "payment") return "Payment";
  if (row?.invoice_number) return `Invoice #${row.invoice_number}`;
  if (row?.stripe_invoice_id) return `Invoice ${row.stripe_invoice_id}`;
  return "Adjustment";
};

const billingDateTime = (value, timeZone) =>
  formatInTimeZone(
    value,
    timeZone,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    "en-CA"
  );

const isCancelableSubscriptionStatus = (value) => {
  const normalized = (value || "").toLowerCase();
  return ["trialing", "active", "past_due", "unpaid", "incomplete"].includes(normalized);
};

export default function BillingHome() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingAddon, setSavingAddon] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [removingDiscount, setRemovingDiscount] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);

  const salonTimeZone =
    summary?.salon_timezone || user?.salon_timezone || "America/Toronto";

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, txData] = await Promise.all([
        getBillingSummary(token),
        getBillingTransactions(token, { limit: 20 }),
      ]);
      setSummary(summaryData);
      setTransactions(Array.isArray(txData?.items) ? txData.items : []);
      setNextCursor(txData?.next_cursor || null);
      setDiscountCodeInput("");
    } catch (err) {
      setSummary(null);
      setTransactions([]);
      setNextCursor(null);
      setError(err?.response?.data?.detail || "Unable to load billing details.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleLoadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const data = await getBillingTransactions(token, {
        cursor: nextCursor,
        limit: 20,
      });
      const rows = Array.isArray(data?.items) ? data.items : [];
      setTransactions((prev) => [...prev, ...rows]);
      setNextCursor(data?.next_cursor || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load more transactions.");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSyncSeats = async () => {
    setSyncing(true);
    setError("");
    setNotice("");
    try {
      await syncBillingSeats(token);
      setNotice("Seat quantity synced.");
      await loadPage();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to sync seat quantity.");
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectTaxAddon = async (taxAddonCode) => {
    if (summary?.billing_test_free || (summary?.status || "").toLowerCase() === "test_free") {
      setError("SalonBusinessCPA packages are not charged on this testing account.");
      setNotice("");
      return;
    }
    setSavingAddon(true);
    setError("");
    setNotice("");
    try {
      const response = await updateBillingTaxAddon(token, taxAddonCode);
      setSummary(response);
      setTransactions(Array.isArray(response?.last_transactions) ? response.last_transactions : []);
      setNextCursor(null);
      setNotice("SalonBusinessCPA package updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update SalonBusinessCPA package.");
    } finally {
      setSavingAddon(false);
    }
  };

  const handleApplyDiscountCode = async () => {
    if (summary?.billing_test_free || (summary?.status || "").toLowerCase() === "test_free") {
      setError("Discount codes are not needed for this testing account.");
      setNotice("");
      return;
    }
    if (!discountCodeInput.trim()) {
      setError("Enter a discount code.");
      setNotice("");
      return;
    }
    setApplyingDiscount(true);
    setError("");
    setNotice("");
    try {
      const response = await applyBillingDiscountCode(token, discountCodeInput.trim());
      setSummary(response);
      setTransactions(Array.isArray(response?.last_transactions) ? response.last_transactions : []);
      setNextCursor(null);
      setDiscountCodeInput("");
      setNotice("Discount code applied.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to apply the discount code.");
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscountCode = async () => {
    if (summary?.billing_test_free || (summary?.status || "").toLowerCase() === "test_free") {
      setError("Discount codes are not needed for this testing account.");
      setNotice("");
      return;
    }
    setRemovingDiscount(true);
    setError("");
    setNotice("");
    try {
      const response = await removeBillingDiscountCode(token);
      setSummary(response);
      setTransactions(Array.isArray(response?.last_transactions) ? response.last_transactions : []);
      setNextCursor(null);
      setDiscountCodeInput("");
      setNotice("Discount code removed.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to remove the discount code.");
    } finally {
      setRemovingDiscount(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelModalOpen(false);
    setCancelingSubscription(true);
    setError("");
    setNotice("");
    try {
      const response = await cancelBillingSubscription(token);
      setSummary(response);
      setTransactions(Array.isArray(response?.last_transactions) ? response.last_transactions : []);
      setNextCursor(null);
      const scheduledEndText =
        response?.cancel_at_utc
          ? billingDateTime(response.cancel_at_utc, salonTimeZone)
          : response?.current_period_end_utc
          ? billingDateTime(response.current_period_end_utc, salonTimeZone)
          : "";
      setNotice(
        scheduledEndText
          ? `Subscription cancellation scheduled for ${scheduledEndText}.`
          : "Subscription cancellation scheduled."
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to cancel the subscription.");
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateModalOpen(false);
    setReactivatingSubscription(true);
    setError("");
    setNotice("");
    try {
      const response = await reactivateBillingSubscription(token);
      setSummary(response);
      setTransactions(Array.isArray(response?.last_transactions) ? response.last_transactions : []);
      setNextCursor(null);
      const restoredBillingText =
        response?.next_billing_utc
          ? billingDateTime(response.next_billing_utc, salonTimeZone)
          : response?.current_period_end_utc
          ? billingDateTime(response.current_period_end_utc, salonTimeZone)
          : "";
      setNotice(
        restoredBillingText
          ? `Subscription kept active. Next billing stays ${restoredBillingText}.`
          : "Subscription kept active."
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to keep the subscription active.");
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const manageCardPath = useMemo(() => {
    if (!summary?.card?.last4) return "/manager/manage/billing/replace-card";
    return "/manager/manage/billing/card";
  }, [summary?.card?.last4]);

  const basePlanPrice = formatCurrencyFromCents(
    summary?.base_price_cents ?? 7999,
    summary?.currency || "cad"
  );
  const taxAddonPrice = formatCurrencyFromCents(
    summary?.tax_addon_price_cents ?? 0,
    summary?.currency || "cad"
  );
  const subtotal = summary?.subtotal_cents == null
    ? "Custom quote"
    : formatCurrencyFromCents(summary?.subtotal_cents ?? 0, summary?.currency || "cad");
  const discountAmount = formatCurrencyFromCents(
    summary?.discount_amount_cents ?? 0,
    summary?.currency || "cad"
  );
  const monthlyRate =
    summary?.monthly_total_cents == null
      ? "Custom quote"
      : formatCurrencyFromCents(summary?.monthly_total_cents ?? 0, summary?.currency || "cad");
  const seatCount = Number(summary?.seat_count || 0);
  const selectedTaxAddonCode = summary?.tax_addon_code ?? null;
  const billingPeriodStaffCount = Number(
    summary?.billing_period_staff_count ?? summary?.billable_seat_count ?? seatCount
  );
  const activeDiscount = summary?.discount || null;
  const accountingPackageSelected = Boolean(
    summary?.accounting_package_selected || summary?.tax_addon_price_cents
  );
  const accountingAccessStatus = String(summary?.accounting_access_status || "").toLowerCase();
  const accountingTrialIsActive = Boolean(summary?.accounting_trial_is_active);
  const accountingTrialHasEnded = Boolean(summary?.accounting_trial_has_ended);
  const isTrialing = (summary?.status || "").toLowerCase() === "trialing";
  const isBillingTestFree =
    Boolean(summary?.billing_test_free) ||
    (summary?.status || "").toLowerCase() === "test_free";
  const hasCardOnFile = Boolean(summary?.card?.last4);
  const cancellationScheduled = Boolean(summary?.cancel_at_period_end);
  const trialEndsText =
    summary?.trial_end_utc
      ? billingDateTime(summary.trial_end_utc, salonTimeZone)
      : summary?.current_period_end_utc && isTrialing
      ? billingDateTime(summary.current_period_end_utc, salonTimeZone)
      : "";
  const accountingTrialEndsText =
    summary?.accounting_trial_end_utc
      ? billingDateTime(summary.accounting_trial_end_utc, salonTimeZone)
      : "";
  const accountingDaysRemaining = Number(summary?.accounting_trial_days_remaining || 0);
  const accountingStatusLabel = accountingPackageSelected
    ? "Paid package active"
    : isBillingTestFree
    ? "Testing account"
    : accountingTrialIsActive
    ? `${accountingDaysRemaining} trial day${accountingDaysRemaining === 1 ? "" : "s"} left`
    : accountingTrialHasEnded || accountingAccessStatus === "trial_expired"
    ? "Trial ended"
    : "Optional add-on";
  const cancellationEndsText =
    summary?.cancel_at_utc
      ? billingDateTime(summary.cancel_at_utc, salonTimeZone)
      : summary?.current_period_end_utc
      ? billingDateTime(summary.current_period_end_utc, salonTimeZone)
      : "";
  const cancelConfirmationMessage =
    isTrialing && trialEndsText
      ? `Your free trial ends on ${trialEndsText}.`
      : cancellationEndsText
      ? `Your subscription will stay active until ${cancellationEndsText} and then stop.`
      : "Schedule subscription cancellation at the end of the current billing period?";
  const cancelConfirmationSubMessage =
    isTrialing && trialEndsText && hasCardOnFile
      ? "Your card will be charged automatically unless you cancel before then. Schedule cancellation now?"
      : cancellationEndsText
      ? "Schedule cancellation now?"
      : "";
  const reactivateConfirmationMessage = cancellationEndsText
    ? `Your scheduled cancellation on ${cancellationEndsText} will be removed.`
    : "Your scheduled cancellation will be removed.";
  const reactivateConfirmationSubMessage =
    "Billing will continue normally and your subscription will stay active.";
  const canCancelSubscription =
    !loading &&
    !cancelingSubscription &&
    !isBillingTestFree &&
    hasCardOnFile &&
    !summary?.custom_quote_required &&
    !cancellationScheduled &&
    isCancelableSubscriptionStatus(summary?.status);
  const canReactivateSubscription =
    !loading &&
    !reactivatingSubscription &&
    !isBillingTestFree &&
    cancellationScheduled &&
    isCancelableSubscriptionStatus(summary?.status);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">Billing</h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div id="billing-overview">
              <ReportHelpCard
                title="Help Guide: Billing"
                subtitle="Manage seats, card status, and transaction history from one page."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}

            {!loading && isBillingTestFree && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                {summary?.billing_free_reason ||
                  "Testing account - billing is waived. No card or subscription is required for this salon."}
              </div>
            )}

            {!loading && !isBillingTestFree && cancellationScheduled && cancellationEndsText && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Your subscription is scheduled to cancel on {cancellationEndsText}. You can
                keep using Ellyanna until then. Your card will not be charged after that date.
                If you changed your mind, use Keep Subscription before then.
              </div>
            )}

            {!loading && !isBillingTestFree && !cancellationScheduled && isTrialing && trialEndsText && hasCardOnFile && (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                Your free trial ends on {trialEndsText}. Your card will be charged
                automatically unless you cancel before then.
              </div>
            )}

            {!loading && !isBillingTestFree && !cancellationScheduled && isTrialing && trialEndsText && !hasCardOnFile && (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                Your free trial ends on {trialEndsText}. You can add billing
                information now, or wait until the trial ends.
              </div>
            )}

            {!loading && !isBillingTestFree && (summary?.status || "").toLowerCase() === "trial_expired" && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Your free trial has ended. Add billing information to continue using Ellyanna.
              </div>
            )}

            {!loading && !isBillingTestFree && summary?.billing_count_note && (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                {summary.billing_count_note} Current active service staff: {seatCount}. Billing-period staff count: {billingPeriodStaffCount}.
              </div>
            )}

            <div
              id="billing-summary"
              className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Ellyanna Booking Subscription
                  </h2>
                  <p className="text-sm text-slate-500">
                    Required salon booking and management software.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSyncSeats}
                    disabled={syncing || loading}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {syncing ? "Syncing..." : "Sync Seats"}
                  </button>
                  {isBillingTestFree ? (
                    <span className="rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700">
                      Billing waived
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(manageCardPath)}
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <span className="inline-flex items-center gap-2">
                        {hasCardOnFile ? "Manage Card on File" : "Add Billing Info"}
                        <InfoTooltip text={TOOLTIPS.manage_card} label="Manage Card on File" />
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate("/manager/manage/billing/statements")}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    <span className="inline-flex items-center gap-2">
                      View Statements
                      <InfoTooltip text={TOOLTIPS.statements} label="View Statements" />
                    </span>
                  </button>
                  {!isBillingTestFree && (cancellationScheduled ? (
                    <button
                      type="button"
                      onClick={() => setReactivateModalOpen(true)}
                      disabled={!canReactivateSubscription}
                      className="rounded-full border border-emerald-300 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {reactivatingSubscription ? "Restoring..." : "Keep Subscription"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCancelModalOpen(true)}
                      disabled={!canCancelSubscription}
                      className="rounded-full border border-rose-300 px-4 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {cancelingSubscription ? "Cancelling..." : "Cancel Subscription"}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="text-sm text-slate-500">Loading billing summary...</div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Current Service Staff
                      <InfoTooltip text={TOOLTIPS.seat_count} label="Current Seat Count" />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">{seatCount}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {summary?.plan_range_label || "1 to 3 salon staff"}
                    </div>
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase text-slate-500">
                        Billing-period staff count
                        <InfoTooltip text={TOOLTIPS.billing_period_staff_count} label="Billing-period Staff Count" />
                      </div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        {billingPeriodStaffCount}
                      </div>
                    </div>
                    {summary?.custom_quote_required && (
                      <div className="mt-2 text-xs font-semibold text-amber-700">
                        Contact Ellyanna for custom pricing above 10 active service staff.
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Base Plan
                      <InfoTooltip text={TOOLTIPS.base_plan} label="Base Plan" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {summary?.plan_label || "Starter"}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">{basePlanPrice} / month</div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Billing Total
                      <InfoTooltip text={TOOLTIPS.monthly_rate} label="Monthly Total" />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">{monthlyRate}</div>
                    {summary?.custom_quote_required ? (
                      <div className="mt-1 text-xs text-slate-500">
                        Custom quote required for 11+ active service staff
                      </div>
                    ) : (
                      <div className="mt-1 space-y-1 text-xs text-slate-500">
                        <div>Ellyanna Booking: {basePlanPrice} / month</div>
                        <div>SalonBusinessCPA optional add-on: {summary?.tax_addon_price_cents ? `${taxAddonPrice} / month` : "$0.00 / month"}</div>
                        {activeDiscount && (
                          <div className="font-medium text-emerald-700">
                            {activeDiscount.description} ({discountAmount} off)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Subtotal
                      <InfoTooltip text={TOOLTIPS.subtotal} label="Subtotal" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">{subtotal}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Before coupon or promotion code discounts.
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Discount
                      <InfoTooltip text={TOOLTIPS.discount} label="Discount" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {activeDiscount ? `-${discountAmount}` : "No discount"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {activeDiscount ? activeDiscount.description : "Apply a coupon code below."}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Subscription Status
                      <InfoTooltip text={TOOLTIPS.status} label="Subscription Status" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {formatStatusLabel(summary?.status)}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {cancellationScheduled && cancellationEndsText
                        ? `Cancellation scheduled: ${cancellationEndsText}`
                        : isBillingTestFree
                        ? "No billing date required"
                        : summary?.next_billing_utc
                        ? `Next billing: ${billingDateTime(summary.next_billing_utc, salonTimeZone)}`
                        : "No next billing date"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Balance
                      <InfoTooltip text={TOOLTIPS.balance} label="Balance" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {formatCurrencyFromCents(
                        summary?.balance_cents ?? 0,
                        summary?.currency || "cad"
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Next Billing Date
                      <InfoTooltip text={TOOLTIPS.next_billing} label="Next Billing Date" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {isBillingTestFree
                        ? "Not required"
                        : summary?.next_billing_utc
                        ? billingDateTime(summary.next_billing_utc, salonTimeZone)
                        : "--"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{salonTimeZone}</div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Ellyanna Booking plan prices</h3>
                  <p className="text-xs text-slate-500">
                    These are the required booking software plans. SalonBusinessCPA accounting services are optional and shown separately below.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                  {PLAN_TIERS.map((plan) => {
                    const isCurrentPlan =
                      (summary?.plan_code || "").toLowerCase() === plan.code ||
                      (summary?.plan_label || "").toLowerCase() === plan.label.toLowerCase();
                    return (
                      <div
                        key={plan.code}
                        className={`rounded-2xl border p-4 ${
                          isCurrentPlan
                            ? "border-rose-300 bg-rose-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-base font-semibold text-slate-900">{plan.label}</div>
                            <div className="mt-1 text-xs text-slate-500">{plan.technicianRange}</div>
                          </div>
                          {isCurrentPlan && (
                            <span className="rounded-full bg-rose-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="mt-4 text-2xl font-semibold text-slate-900">
                          {formatCurrencyFromCents(plan.priceCents, summary?.currency || "cad")}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">per month</div>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                  Salons with more than 10 active service staff move to custom quote billing.
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex-1 space-y-2">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Discount code</h3>
                      <p className="text-xs text-slate-500">
                        Enter a discount code provided by Ellyanna for your subscription.
                      </p>
                    </div>
                    <label className="block">
                      <span className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Coupon code
                        <InfoTooltip text={TOOLTIPS.discount_code} label="Discount code" />
                      </span>
                      <input
                        type="text"
                        value={discountCodeInput}
                        onChange={(event) => setDiscountCodeInput(event.target.value.toUpperCase())}
                        placeholder="Enter coupon code"
                        disabled={
                          loading ||
                          syncing ||
                          applyingDiscount ||
                          removingDiscount ||
                          isBillingTestFree ||
                          !!summary?.custom_quote_required
                        }
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-100"
                      />
                    </label>
                    {activeDiscount && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                        <div className="font-semibold">{activeDiscount.code}</div>
                        <div>{activeDiscount.description}</div>
                      </div>
                    )}
                    {summary?.custom_quote_required && (
                      <div className="text-xs font-medium text-amber-700">
                        Discount codes are not available while billing is on custom quote pricing.
                      </div>
                    )}
                    {isBillingTestFree && (
                      <div className="text-xs font-medium text-emerald-700">
                        Discount codes are not needed while billing is waived for this testing account.
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={handleApplyDiscountCode}
                      disabled={
                        loading ||
                        syncing ||
                        applyingDiscount ||
                        removingDiscount ||
                        !discountCodeInput.trim() ||
                        isBillingTestFree ||
                        !!summary?.custom_quote_required
                      }
                      className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      {applyingDiscount ? "Applying..." : activeDiscount ? "Replace Discount" : "Apply Discount"}
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveDiscountCode}
                      disabled={
                        loading ||
                        syncing ||
                        applyingDiscount ||
                        removingDiscount ||
                        isBillingTestFree ||
                        !activeDiscount
                      }
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {removingDiscount ? "Removing..." : "Remove Discount"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Optional add-on
                    </div>
                    <h3 className="mt-3 text-base font-semibold text-slate-900">
                      SalonBusinessCPA Accounting Services
                    </h3>
                    <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
                      SalonBusinessCPA is separate from your required Ellyanna Booking plan.
                      Try it free for 30 days. After the trial, choose a paid
                      SalonBusinessCPA package to keep using the accounting portal.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm">
                    <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                      Accounting access
                    </div>
                    <div className="mt-1 font-semibold text-slate-900">{accountingStatusLabel}</div>
                    {accountingTrialIsActive && accountingTrialEndsText ? (
                      <div className="mt-1 text-xs text-slate-500">
                        Trial ends {accountingTrialEndsText}
                      </div>
                    ) : null}
                    {accountingTrialHasEnded && !accountingPackageSelected ? (
                      <div className="mt-1 text-xs text-rose-700">
                        Choose a paid package to continue using SalonBusinessCPA.
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Current optional selection
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {summary?.tax_addon_label || "No SalonBusinessCPA package selected"}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      {summary?.tax_addon_price_cents
                        ? `${taxAddonPrice} / month`
                        : "$0.00 / month"}
                    </div>
                    <div className="mt-3 text-xs leading-5 text-slate-500">
                      Ellyanna Booking remains active separately. SalonBusinessCPA
                      access continues only while the accounting trial is active or
                      a paid package is selected.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Monthly breakdown
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-600">Ellyanna Booking required plan</span>
                        <span className="font-semibold text-slate-900">{basePlanPrice}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-600">SalonBusinessCPA optional add-on</span>
                        <span className="font-semibold text-slate-900">
                          {summary?.tax_addon_price_cents ? taxAddonPrice : "$0.00"}
                        </span>
                      </div>
                      <div className="border-t border-slate-200 pt-2 flex items-center justify-between gap-4">
                        <span className="font-semibold text-slate-900">Monthly total</span>
                        <span className="font-semibold text-slate-900">{monthlyRate}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {TAX_ADDON_OPTIONS.map((option) => {
                    const isSelected = selectedTaxAddonCode === option.code;
                    return (
                      <button
                        key={option.code || "none"}
                        type="button"
                        onClick={() => handleSelectTaxAddon(option.code)}
                        disabled={loading || savingAddon || syncing || isBillingTestFree || summary?.custom_quote_required}
                        className={`min-h-[112px] rounded-2xl border p-4 text-left transition ${
                          isSelected
                            ? "border-rose-500 bg-white shadow-sm ring-2 ring-rose-100"
                            : "border-slate-200 bg-white hover:border-emerald-300"
                        } disabled:opacity-60`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                            <div className="mt-1 text-xs text-slate-500">
                              {option.range || "No optional accounting package selected"}
                            </div>
                          </div>
                          {isSelected && (
                            <span className="rounded-full bg-rose-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Selected
                            </span>
                          )}
                        </div>
                        <div className="mt-4 text-xl font-semibold text-slate-900">
                          {option.priceCents > 0
                            ? `${formatCurrencyFromCents(option.priceCents, summary?.currency || "cad")} / month`
                            : "$0.00 / month"}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {summary?.custom_quote_required && (
                  <div className="text-xs font-medium text-amber-700">
                    SalonBusinessCPA packages stay visible here, but salon billing above 10 active service staff requires a manual quote.
                  </div>
                )}
                {isBillingTestFree && (
                  <div className="text-xs font-medium text-emerald-700">
                    SalonBusinessCPA packages are not charged on this testing account.
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <span className="inline-flex items-center gap-2">
                  Plan tier is based on the billing-period staff count for active staff who perform services.
                  <InfoTooltip text={TOOLTIPS.seat_rules} label="Seat Count Rules" />
                </span>
              </div>
            </div>

            <div
              id="billing-transactions"
              className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4"
            >
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Transaction History
                </h2>
                <p className="text-sm text-slate-500">
                  Invoice and payment activity shown in salon timezone.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left w-56">
                        <span className="inline-flex items-center gap-2">
                          Date
                          <InfoTooltip text={TOOLTIPS.transaction_date} label="Date" />
                        </span>
                      </th>
                      <th className="px-3 py-2 text-left">Transaction</th>
                      <th className="px-3 py-2 text-right w-44">
                        <span className="inline-flex items-center justify-end gap-2">
                          Amount
                          <InfoTooltip text={TOOLTIPS.transaction_amount} label="Amount" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {loading && (
                      <tr>
                        <td colSpan={3} className="px-3 py-4 text-slate-500">
                          Loading transactions...
                        </td>
                      </tr>
                    )}
                    {!loading && transactions.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-4 text-slate-500">
                          No transactions found.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      transactions.map((row) => {
                        const amount = Number(row?.amount_cents || 0);
                        const amountClass =
                          amount < 0 ? "text-emerald-700" : "text-slate-900";
                        const hasInvoiceLink = Boolean(
                          row?.hosted_invoice_url && row?.type !== "payment"
                        );
                        return (
                          <tr
                            key={row.id}
                            className={`hover:bg-slate-50 ${hasInvoiceLink ? "cursor-pointer" : ""}`}
                            onClick={() => {
                              if (!hasInvoiceLink) return;
                              window.open(row.hosted_invoice_url, "_blank", "noopener,noreferrer");
                            }}
                          >
                            <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                              {billingDateTime(row.occurred_at_utc, salonTimeZone)}
                            </td>
                            <td className="px-3 py-2.5 text-slate-700">
                              <span className={hasInvoiceLink ? "underline" : ""}>
                                {formatTransactionLabel(row)}
                              </span>
                            </td>
                            <td className={`px-3 py-2.5 text-right font-semibold ${amountClass}`}>
                              {formatCurrencyFromCents(amount, row?.currency || summary?.currency)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {nextCursor && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={cancelModalOpen}
        title="Cancel Subscription"
        message={cancelConfirmationMessage}
        subMessage={cancelConfirmationSubMessage}
        confirmText={cancelingSubscription ? "Scheduling..." : "Schedule Cancellation"}
        cancelText="Keep Subscription"
        onClose={() => {
          if (cancelingSubscription) return;
          setCancelModalOpen(false);
        }}
        onConfirm={handleCancelSubscription}
        isBusy={cancelingSubscription}
      />
      <ConfirmModal
        open={reactivateModalOpen}
        title="Keep Subscription"
        message={reactivateConfirmationMessage}
        subMessage={reactivateConfirmationSubMessage}
        confirmText={reactivatingSubscription ? "Restoring..." : "Keep Subscription"}
        cancelText="Close"
        onClose={() => {
          if (reactivatingSubscription) return;
          setReactivateModalOpen(false);
        }}
        onConfirm={handleReactivateSubscription}
        isBusy={reactivatingSubscription}
      />
    </div>
  );
}
